'use client';

import React, { useState, useEffect, useRef } from 'react';

import { Input } from '@/app/components/ui/Input';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface CharityAutocompleteProps {
  onCharitySelect: (charity: { name: string; number: string }) => void;
  defaultValue?: string;
  placeholder?: string;
  className?: string;
  // Compatible with react-hook-form register props
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

export const CharityAutocomplete = ({ 
  onCharitySelect, 
  defaultValue = '', 
  placeholder = "Start typing charity name...",
  className,
  value,
  onChange,
  onBlur,
  name,
  disabled
}: CharityAutocompleteProps) => {
  // If controlled (value is provided), use it; otherwise use local state
  const [inputValue, setInputValue] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  
  // Track if we just selected an item to prevent immediate re-search
  const [justSelected, setJustSelected] = useState(false);

  // Sync local state with controlled value
  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // If we just selected an item, reset the flag and DO NOT search
    if (justSelected) {
      setSuggestions([]); // Clear suggestions to ensure dropdown doesn't reappear
      setJustSelected(false);
      return;
    }

    // Don't search if the input matches what we just selected (simple heuristic)
    // or if it's too short
    if (!inputValue || inputValue.length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchCharities = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/charity/search?q=${encodeURIComponent(inputValue)}`);
        const data = await res.json();
        const list = data.list || [];
        setSuggestions(list);
        if (list.length > 0) setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching charities:', error);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchCharities, 300);
    return () => clearTimeout(timer);
  }, [inputValue]); // justSelected is excluded from dependency array to not trigger re-run when it changes alone, but it's checked inside

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setJustSelected(false); // Enable searching again if user types
    onChange?.(e); // Propagate to react-hook-form
  };

  const handleSelect = (charity: any) => {
    const name = charity.name;
    const number = charity.number;
    
    setJustSelected(true); // Flag to prevent effect from firing search
    setInputValue(name);
    setSuggestions([]); // Clear suggestions explicitly
    setShowSuggestions(false);
    
    onCharitySelect({ name, number });
    
    // Ensure react-hook-form knows about the update
    if (onChange) {
        const event = {
            target: { value: name, name: name },
            type: 'change'
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onBlur={onBlur}
          name={name}
          placeholder={placeholder}
          className={cn("pr-10", className)}
          autoComplete="off"
          disabled={disabled}
        />
        {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full bg-white border border-border rounded-[6px] shadow-lg mt-1 max-h-60 overflow-auto">
          {suggestions.map((charity: any, index: number) => {
             // Fallback key if number is missing or duplicated
             const key = charity.number ? `${charity.number}-${index}` : `charity-${index}`;
             return (
                <li
                  key={key}
                  onClick={() => handleSelect(charity)}
                  className="px-4 py-2 hover:bg-secondary/50 cursor-pointer text-sm border-b border-gray-100 last:border-0"
                >
                  <div className="font-medium text-gray-900">{charity.name}</div>
                  <div className="text-xs text-gray-500">Reg: {charity.number}</div>
                </li>
             );
          })}
        </ul>
      )}
    </div>
  );
};
